<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div
      ref="scrollContainer"
      class="z-10 min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pt-6 pb-20"
    >
      <div
        v-if="!messages.length"
        class="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-gray-400"
      >
        <AiAssistantAvatar :playful="!$route.meta.avatarSerious" :size="64" />
        <p>
          {{
            $t(
              'global.ai_assistant.empty_state',
              'Pose une question pour démarrer la conversation.'
            )
          }}
        </p>
      </div>

      <div
        v-for="message in visibleMessages"
        :key="message.id"
        class="flex"
        :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          v-if="message.role === 'user'"
          class="max-w-[85%] squircle-2xl squircle-tr-sm border border-gray-50 bg-gray-100 px-4 py-2.5 text-sm text-gray-800 shadow-md dark:bg-gray-800 dark:text-neutral-100"
        >
          {{ message.content }}
        </div>
        <div v-else-if="message.role === 'tool'" class="flex max-w-[92%] gap-3">
          <div
            class="flex items-center gap-2 text-xs font-medium"
            :class="
              message.status === 'running'
                ? 'text-slate-800 dark:text-slate-300'
                : 'text-slate-400 dark:text-slate-500'
            "
          >
            <FontAwesomeIcon
              :icon="[
                'fal',
                message.status === 'running' ? 'spinner-third' : 'check'
              ]"
              :spin="message.status === 'running'"
              :class="
                message.status === 'running'
                  ? 'text-slate-800 dark:text-slate-300'
                  : 'text-slate-400 dark:text-slate-500'
              "
            />
            <span>{{ formatToolName(message.name) }}</span>
          </div>
        </div>
        <div v-else-if="message.role === 'widget'" class="max-w-[92%]">
          <AiAssistantWidgetCard :message="message" />
        </div>
        <div
          v-else-if="message.role === 'approval'"
          class="flex w-full max-w-sm items-start gap-2 squircle-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <div
            class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
          >
            <FontAwesomeIcon :icon="['fal', 'pen']" />
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="text-sm whitespace-pre-line text-gray-900 dark:text-gray-100"
            >
              {{
                message.summary ??
                $t(
                  'global.ai_assistant.approvals.generic',
                  "L'assistant veut modifier des données."
                )
              }}
            </p>
            <div v-if="message.status === 'pending'" class="mt-2 flex gap-2">
              <button
                type="button"
                class="rounded-lg bg-black px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-50 dark:bg-white dark:text-black"
                :disabled="isSending"
                @click="decideApproval(message, true)"
              >
                {{ $t('global.ai_assistant.approvals.approve', 'Valider') }}
              </button>
              <button
                type="button"
                class="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                :disabled="isSending"
                @click="decideApproval(message, false)"
              >
                {{ $t('global.ai_assistant.approvals.reject', 'Refuser') }}
              </button>
            </div>
            <p
              v-else
              class="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
            >
              <FontAwesomeIcon
                :icon="['fal', APPROVAL_STATUSES[message.status].icon]"
              />
              <span>
                {{
                  $t(
                    `global.ai_assistant.approvals.${message.status}`,
                    APPROVAL_STATUSES[message.status].fallback
                  )
                }}
              </span>
            </p>
          </div>
        </div>
        <div v-else class="flex max-w-[92%] min-w-0 flex-col gap-1.5">
          <div
            class="ai-response-prose prose prose-sm max-w-none leading-tight text-gray-800 dark:text-gray-100 dark:prose-invert"
            v-html="renderMarkdown(message.content)"
          />
          <button
            type="button"
            class="flex w-fit items-center gap-1.5 rounded-lg px-1.5 py-1 text-xs text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            @click="copyMessage(message)"
          >
            <FontAwesomeIcon
              :icon="[
                'fal',
                copiedMessageId === message.id ? 'check' : 'paperclip'
              ]"
            />
            <span>
              {{
                copiedMessageId === message.id
                  ? $t('global.ai_assistant.copied', 'Copié !')
                  : $t('global.ai_assistant.copy', 'Copier')
              }}
            </span>
          </button>
        </div>
      </div>

      <div v-if="showTyping" class="flex items-start">
        <div class="flex items-center gap-1 pt-1">
          <span
            class="h-1.5 w-1.5 animate-small-bounce rounded-full bg-emerald-400"
          />
          <span
            class="h-1.5 w-1.5 animate-small-bounce rounded-full bg-emerald-400"
            style="animation-delay: 150ms"
          />
          <span
            class="h-1.5 w-1.5 animate-small-bounce rounded-full bg-emerald-400"
            style="animation-delay: 300ms"
          />
        </div>
      </div>
    </div>

    <div
      class="absolute bottom-0 left-0 z-10 w-full pt-10 pb-6 dark:from-gray-900 dark:via-gray-900/80"
    >
      <div class="mx-auto flex max-w-3xl flex-col gap-2 px-4">
        <AlertPanel
          v-if="quotaExceeded"
          type="danger"
          compact
          :title="
            $t(
              'attributes.ai_quota_exceeded',
              { date: quotaResetsAt },
              'Quota IA mensuel atteint, renouvellement le {date}'
            )
          "
        />
        <AlertPanel
          v-else-if="quota?.warning"
          type="warning"
          compact
          :title="
            $t(
              'attributes.ai_quota_warning',
              { percentage: quota.percentage },
              '{percentage} % du quota IA mensuel utilisé'
            )
          "
        />
        <div
          class="relative flex items-center rounded-2xl border border-gray-300 bg-gray-50 shadow-lg focus-within:border-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-gray-600"
        >
          <!-- Textarea input -->
          <textarea
            v-model="draft"
            :disabled="quotaExceeded"
            :placeholder="
              $t(
                'global.ai_assistant.input_placeholder',
                'Écris ta question...'
              )
            "
            class="field-sizing-content max-h-[10lh] min-h-[1lh] w-full resize-none overflow-y-auto bg-transparent py-3.5 pr-24 pl-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder-gray-500"
            @keydown.enter.exact.prevent="sendMessage"
          ></textarea>

          <div class="absolute right-3 flex items-center space-x-2">
            <button
              type="button"
              class="rounded-xl bg-black p-1.5 text-white transition-opacity hover:opacity-80 dark:bg-white dark:text-black"
              :disabled="!draft.trim() || isSending || quotaExceeded"
              @click="sendMessage"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 12h14M12 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AiAssistantAvatar from '@/views/layouts/main-layout/ai-assistant/AiAssistantAvatar.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import AlertPanel from '@ui/AlertPanel.vue'
import AiAssistantWidgetCard from '@/views/layouts/main-layout/ai-assistant/AiAssistantWidgetCard.vue'
import { isKnownWidgetResource } from '@/views/layouts/main-layout/ai-assistant/aiAssistantWidgets'
import { showSendError } from '@/views/layouts/main-layout/ai-assistant/aiAssistantSendErrors'

marked.setOptions({ breaks: true, gfm: true })

// Fallbacks until the Lokalise keys are synced.
const APPROVAL_STATUSES = {
  approved: { icon: 'circle-check', fallback: 'Validé' },
  rejected: { icon: 'circle-xmark', fallback: 'Refusé' },
  skipped: {
    icon: 'ban',
    fallback: 'Non appliqué : la conversation a continué'
  }
}

export default {
  name: 'AiAssistantConversation',
  components: {
    AlertPanel,
    FontAwesomeIcon,
    AiAssistantAvatar,
    AiAssistantWidgetCard
  },
  data() {
    return {
      draft: '',
      copiedMessageId: null,
      APPROVAL_STATUSES
    }
  },
  computed: {
    ...mapGetters({
      messages: 'aiAssistant/messages',
      isSending: 'aiAssistant/isSending',
      quota: 'aiAssistant/getQuota'
    }),
    quotaExceeded() {
      return this.quota?.percentage != null && this.quota.percentage >= 100
    },
    quotaResetsAt() {
      return this.$h.momentLocalised(this.quota?.resets_at, 'DD/MM/YYYY')
    },
    visibleMessages() {
      return this.messages.filter(
        (message) =>
          message.role !== 'widget' ||
          isKnownWidgetResource(message.data?.resource_type)
      )
    },
    showTyping() {
      if (!this.isSending) {
        return false
      }
      const last = this.messages[this.messages.length - 1]
      if (!last || last.role === 'user') {
        return true
      }
      if (last.role === 'tool') {
        return last.status === 'done'
      }
      if (last.role === 'widget' || last.role === 'approval') {
        return true
      }
      return !last.content.length
    }
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        this.scrollToBottom()
      }
    }
  },
  mounted() {
    this.scrollToBottom()
  },
  methods: {
    ...mapActions({
      dispatchSendMessage: 'aiAssistant/sendMessage',
      dispatchDecideApproval: 'aiAssistant/decideApproval'
    }),
    async decideApproval(message, approved) {
      if (this.isSending) {
        return
      }
      const error = await this.dispatchDecideApproval({
        id: message.id,
        approved
      })
      if (error) {
        this.showSendError(error)
      }
    },
    async sendMessage() {
      const content = this.draft.trim()
      if (!content || this.isSending || this.quotaExceeded) {
        return
      }
      this.draft = ''
      const error = await this.dispatchSendMessage(content)
      if (error) {
        this.showSendError(error)
      }
    },
    showSendError(error) {
      showSendError(this, error)
    },
    renderMarkdown(content) {
      return DOMPurify.sanitize(marked.parse(content ?? ''))
    },
    formatToolName(name) {
      return name?.replace(/[-_]/g, ' ') ?? ''
    },
    async copyMessage(message) {
      try {
        await navigator.clipboard.writeText(message.content ?? '')
        this.copiedMessageId = message.id
        setTimeout(() => {
          if (this.copiedMessageId === message.id) {
            this.copiedMessageId = null
          }
        }, 2000)
      } catch {
        // clipboard access denied, ignore silently
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    }
  }
}
</script>

<style scoped>
.ai-response-prose :deep(p) {
  margin-top: 0.4em;
  margin-bottom: 0.4em;
}

.ai-response-prose :deep(ul),
.ai-response-prose :deep(ol) {
  margin-top: 0.4em;
  margin-bottom: 0.4em;
}

.ai-response-prose :deep(li) {
  margin-top: 0.15em;
  margin-bottom: 0.15em;
}

.ai-response-prose :deep(li > p) {
  margin-top: 0.15em;
  margin-bottom: 0.15em;
}

.ai-response-prose :deep(h1),
.ai-response-prose :deep(h2),
.ai-response-prose :deep(h3),
.ai-response-prose :deep(h4) {
  margin-top: 0.9em;
  margin-bottom: 0.3em;
}

.ai-response-prose :deep(pre) {
  margin-top: 0.6em;
  margin-bottom: 0.6em;
}

.ai-response-prose :deep(blockquote) {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.ai-response-prose :deep(hr) {
  margin-top: 1em;
  margin-bottom: 1em;
}

.ai-response-prose :deep(> :first-child) {
  margin-top: 0;
}

.ai-response-prose :deep(> :last-child) {
  margin-bottom: 0;
}
</style>
